import React, { useState } from 'react';
import PropTypes from 'prop-types';

/**
 * DocumentPreview Component
 * Displays a preview of a document/certificate file with options to view full document
 */
const DocumentPreview = ({ fileUrl, altText }) => {
  const [showFullPreview, setShowFullPreview] = useState(false);
  
  const toggleFullPreview = () => setShowFullPreview(!showFullPreview);
  
  // Determine file type from URL
  const isImageFile = fileUrl && 
    (fileUrl.toLowerCase().endsWith('.jpg') || 
     fileUrl.toLowerCase().endsWith('.jpeg') || 
     fileUrl.toLowerCase().endsWith('.png') ||
     fileUrl.toLowerCase().endsWith('.gif'));
  
  const isPdfFile = fileUrl && fileUrl.toLowerCase().endsWith('.pdf');
  
  return (
    <div className="document-preview">
      {showFullPreview ? (
        <div className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center p-4">
          <div className="relative bg-white rounded-lg max-w-4xl w-full max-h-screen overflow-auto">
            <button 
              onClick={toggleFullPreview}
              className="absolute top-2 right-2 z-10 bg-white rounded-full p-1 shadow-md"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            {isImageFile ? (
              <img 
                src={fileUrl} 
                alt={altText || "Document preview"} 
                className="w-full h-auto"
              />
            ) : isPdfFile ? (
              <iframe
                src={`${fileUrl}#toolbar=0&navpanes=0`}
                title={altText || "PDF Document"}
                className="w-full h-screen"
              />
            ) : (
              <div className="p-8 text-center">
                <p className="mb-4">Preview not available for this file type</p>
                <a 
                  href={fileUrl} 
                  target="_blank" 
                  rel="noreferrer" 
                  className="bg-primary-600 hover:bg-primary-700 text-white py-2 px-4 rounded"
                >
                  Download File
                </a>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div 
          className="relative border border-gray-200 rounded-lg overflow-hidden bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
          onClick={toggleFullPreview}
        >
          {isImageFile ? (
            <div className="aspect-w-3 aspect-h-2">
              <img 
                src={fileUrl} 
                alt={altText || "Document thumbnail"} 
                className="object-contain w-full h-40"
              />
            </div>
          ) : isPdfFile ? (
            <div className="flex flex-col items-center justify-center p-4 h-40">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-red-500 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              <p className="text-sm font-medium">PDF Document</p>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center p-4 h-40">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-sm font-medium">Document</p>
            </div>
          )}
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 hover:bg-opacity-20 transition-all">
            <div className="bg-white bg-opacity-80 rounded-full p-2 transform scale-0 group-hover:scale-100 transition-transform">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

DocumentPreview.propTypes = {
  fileUrl: PropTypes.string.isRequired,
  altText: PropTypes.string
};

export default DocumentPreview;
